import React, { useCallback, useEffect, useState } from 'react';
import { X, FileDown, Loader2, CheckCircle2, AlertCircle, ExternalLink, BookOpen, ChevronDown, Tag, FolderTree } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { runTool } from '../../services/toolRunner';
interface ConfluenceExportModalProps {
  content: string;
  onClose: () => void;
}
interface SpaceOption {
  id: string;
  key: string;
  name: string;
}
interface PageOption {
  id: string;
  title: string;
}
/**
 * Converts Markdown to Confluence Storage Format (XHTML-based)
 */
function markdownToConfluenceStorage(md: string): string {
  let html = md;
  // 1. Code blocks (must be done first to avoid processing contents)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => `<ac:structured-macro ac:name="code"><ac:parameter ac:name="language">${lang || 'text'}</ac:parameter><ac:plain-text-body><![CDATA[${code.trim()}]]></ac:plain-text-body></ac:structured-macro>`);
  // 2. Blockquotes -> Confluence Panel (info style)
  html = html.replace(/^>\s+(.+)$/gm, '<ac:structured-macro ac:name="panel"><ac:rich-text-body><p>$1</p></ac:rich-text-body></ac:structured-macro>');
  // 3. Headers
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  // 4. Horizontal Rules
  html = html.replace(/^---$/gm, '<hr/>');
  // 5. Bold and italic
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  // 6. Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  // 7. Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  // 8. Tables
  // A simple markdown table parser
  const lines = html.split('\n');
  let inTable = false;
  const processedLines: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Check if it's a table row
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      if (!inTable) {
        processedLines.push('<table><tbody>');
        inTable = true;
      }
      // Skip separator row (e.g., |---|---|)
      if (line.replace(/[\s|-]/g, '') === '') {
        continue;
      }
      const cells = line.split('|').slice(1, -1).map(c => c.trim());
      // If it's the first row of the table, treat as headers
      const isHeader = processedLines[processedLines.length - 1] === '<table><tbody>';
      processedLines.push('<tr>');
      for (const cell of cells) {
        if (isHeader) {
          processedLines.push(`<th>${cell}</th>`);
        } else {
          processedLines.push(`<td>${cell}</td>`);
        }
      }
      processedLines.push('</tr>');
    } else {
      if (inTable) {
        processedLines.push('</tbody></table>');
        inTable = false;
      }
      processedLines.push(line);
    }
  }
  if (inTable) {
    processedLines.push('</tbody></table>');
  }
  html = processedLines.join('\n');
  // 9. Unordered lists
  const listLines = html.split('\n');
  let inList = false;
  const finalLines: string[] = [];
  for (const line of listLines) {
    // Skip lines that are already HTML blocks
    if (line.trim().startsWith('<') && !line.trim().startsWith('<strong>') && !line.trim().startsWith('<em>')) {
      if (inList) {
        finalLines.push('</ul>');
        inList = false;
      }
      finalLines.push(line);
      continue;
    }
    const listMatch = line.match(/^[\s]*[-*•]\s+(.+)$/);
    if (listMatch) {
      if (!inList) {
        finalLines.push('<ul>');
        inList = true;
      }
      finalLines.push(`<li>${listMatch[1]}</li>`);
    } else {
      if (inList) {
        finalLines.push('</ul>');
        inList = false;
      }
      // Wrap plain text lines in <p> tags (skip if already HTML)
      if (line.trim() && !line.trim().startsWith('<')) {
        finalLines.push(`<p>${line}</p>`);
      } else {
        finalLines.push(line);
      }
    }
  }
  if (inList) finalLines.push('</ul>');
  html = finalLines.join('\n');
  // Clean up empty paragraphs and double newlines
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/\n{3,}/g, '\n\n');
  return html;
}
function extractTitleFromContent(content: string): string {
  const headingMatch = content.match(/^#+\s+(.+)$/m);
  if (headingMatch) return headingMatch[1].slice(0, 80);
  const firstLine = content.split('\n').find(l => l.trim().length > 0);
  if (firstLine) {
    const clean = firstLine.replace(/[*_#`]/g, '').trim();
    return clean.length > 80 ? clean.slice(0, 77) + '...' : clean;
  }
  return 'Exportación desde Agent Chat';
}
export function ConfluenceExportModal({
  content,
  onClose
}: ConfluenceExportModalProps) {
  const [title, setTitle] = useState(() => extractTitleFromContent(content));
  const [spaceKey, setSpaceKey] = useState('');
  const [spaceId, setSpaceId] = useState('');
  const [parentId, setParentId] = useState<string>('');
  const [labels, setLabels] = useState('');
  const [spaces, setSpaces] = useState<SpaceOption[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(true);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    url?: string;
  } | null>(null);
  // Fetch available spaces
  useEffect(() => {
    let cancelled = false;
    async function fetchSpaces() {
      setSpacesLoading(true);
      try {
        const res = await runTool('confluence_get_spaces', {
          limit: 250
        });
        if (cancelled) return;
        if (res.success && res.data) {
          const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.results) ? res.data.results : [];
          const mapped = data.map((s: any) => ({
            id: String(s.id || ''),
            key: String(s.key || ''),
            name: String(s.name || s.key || '')
          }));
          setSpaces(mapped);
          if (mapped.length > 0) {
            setSpaceKey(mapped[0].key);
            setSpaceId(mapped[0].id);
          }
        }
      } catch {
        // Fallback handled in UI
      } finally {
        if (!cancelled) setSpacesLoading(false);
      }
    }
    fetchSpaces();
    return () => {
      cancelled = true;
    };
  }, []);
  // Fetch pages when space changes
  useEffect(() => {
    if (!spaceId) {
      setPages([]);
      setParentId('');
      return;
    }
    let cancelled = false;
    async function fetchPages() {
      setPagesLoading(true);
      try {
        const res = await runTool('confluence_get_pages_in_space', {
          spaceId: spaceId,
          limit: 50
        });
        if (cancelled) return;
        if (res.success && res.data) {
          const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.results) ? res.data.results : [];
          const mapped = data.map((p: any) => ({
            id: String(p.id || ''),
            title: String(p.title || 'Sin título')
          }));
          setPages(mapped);
          setParentId(''); // Reset parent when space changes
        }
      } catch {
        setPages([]);
      } finally {
        if (!cancelled) setPagesLoading(false);
      }
    }
    fetchPages();
    return () => {
      cancelled = true;
    };
  }, [spaceId]);
  const handleSpaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedKey = e.target.value;
    setSpaceKey(selectedKey);
    const selectedSpace = spaces.find(s => s.key === selectedKey);
    if (selectedSpace) {
      // Use key instead of numeric id — Confluence MCP expects space key
      setSpaceId(selectedSpace.key || selectedSpace.id);
    }
  };
  const handleExport = useCallback(async () => {
    if (!title.trim() || !spaceId) return;
    setExporting(true);
    setResult(null);
    try {
      const storageHtml = markdownToConfluenceStorage(content);
      // 1. Create the page
      const createArgs: any = {
        spaceId: spaceId,
        title: title.trim(),
        body: storageHtml
      };
      if (parentId) {
        createArgs.parentId = parentId;
      }
      const res = await runTool('confluence_create_page', createArgs);
      if (res.success) {
        const pageData = res.data || {};
        const newPageId = pageData.id;
        const baseUrl = pageData._links?.base || '';
        const webui = pageData._links?.webui || '';
        const url = baseUrl && webui ? `${baseUrl.includes('http') ? baseUrl : 'https://' + baseUrl}${webui}` : '';
        // 2. Add labels if provided
        const labelList = labels.split(',').map(l => l.trim()).filter(l => l.length > 0);
        let labelsAdded = 0;
        if (labelList.length > 0 && newPageId) {
          for (const label of labelList) {
            try {
              await runTool('confluence_add_page_label', {
                contentId: newPageId,
                labelName: label
              });
              labelsAdded++;
            } catch (e) {
              console.warn(`Failed to add label: ${label}`, e);
            }
          }
        }
        let msg = `Página "${title}" creada exitosamente en ${spaceKey}.`;
        if (labelsAdded > 0) {
          msg += ` Se agregaron ${labelsAdded} etiquetas.`;
        }
        setResult({
          success: true,
          message: msg,
          url
        });
      } else {
        setResult({
          success: false,
          message: res.error || 'Error al crear la página en Confluence'
        });
      }
    } catch (err: any) {
      setResult({
        success: false,
        message: err.message || 'Error de conexión con Confluence'
      });
    } finally {
      setExporting(false);
    }
  }, [title, spaceId, spaceKey, parentId, labels, content]);
  return <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-id="element-2038">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()} data-id="element-2039">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-800 shrink-0" data-id="element-2040">
          <div className="flex items-center gap-3" data-id="element-2041">
            <div className="p-2 bg-blue-950/50 rounded-xl border border-blue-900/50" data-id="element-2042">
              <BookOpen size={18} className="text-blue-400" data-id="element-2043" />
            </div>
            <div data-id="element-2044">
              <h3 className="text-sm font-bold text-slate-100" data-id="element-2045">
                Exportar a Confluence
              </h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-2046">
                Crear una nueva página con este contenido
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded-lg transition-colors" data-id="element-2047">
            <X size={16} data-id="element-2048" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1 custom-scrollbar" data-id="element-2049">
          {result ? <div className={`flex items-start gap-3 p-4 rounded-xl border ${result.success ? 'bg-emerald-950/30 border-emerald-900/40' : 'bg-red-950/30 border-red-900/40'}`} data-id="element-2050">
              {result.success ? <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" data-id="element-2051" /> : <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" data-id="element-2052" />}
              <div className="flex-1 min-w-0" data-id="element-2053">
                <p className={`text-sm font-medium ${result.success ? 'text-emerald-300' : 'text-red-300'}`} data-id="element-2054">
                  {result.success ? '¡Exportación Exitosa!' : 'Error de Exportación'}
                </p>
                <p className={`text-xs mt-1 ${result.success ? 'text-emerald-400/70' : 'text-red-400/70'}`} data-id="element-2055">
                  {result.message}
                </p>
                {result.url && <a href={result.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-emerald-900/50 hover:bg-emerald-800/50 text-emerald-300 rounded-lg text-xs font-medium transition-colors" data-id="element-2056">
                    <ExternalLink size={12} data-id="element-2057" />
                    Abrir en Confluence
                  </a>}
              </div>
            </div> : <div className="space-y-5" data-id="element-2058">
              {/* Row 1: Space & Parent Page */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2059">
                {/* Space selector */}
                <div data-id="element-2060">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5" data-id="element-2061">
                    <BookOpen size={12} data-id="element-2062" /> Espacio
                  </label>
                  {spacesLoading ? <div className="flex items-center gap-2 text-xs text-slate-500 py-2.5 px-3 bg-slate-800/50 rounded-lg border border-slate-700/50" data-id="element-2063">
                      <Loader2 size={12} className="animate-spin" data-id="element-2064" />
                      Cargando espacios...
                    </div> : spaces.length > 0 ? <div className="relative" data-id="element-2065">
                      <select value={spaceKey} onChange={handleSpaceChange} className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 pr-8 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none" data-id="element-2066">
                        {spaces.map(s => <option key={s.key} value={s.key} data-id="element-2067">
                            {s.name} ({s.key})
                          </option>)}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-2068" />
                    </div> : <input type="text" value={spaceKey} onChange={e => setSpaceKey(e.target.value.toUpperCase())} placeholder="Ej: ENG, PROD" className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2069" />}
                </div>

                {/* Parent Page selector */}
                <div data-id="element-2070">
                  <label className="block text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5" data-id="element-2071">
                    <FolderTree size={12} data-id="element-2072" /> Página Padre (Opcional)
                  </label>
                  {pagesLoading ? <div className="flex items-center gap-2 text-xs text-slate-500 py-2.5 px-3 bg-slate-800/50 rounded-lg border border-slate-700/50" data-id="element-2073">
                      <Loader2 size={12} className="animate-spin" data-id="element-2074" />
                      Cargando páginas...
                    </div> : <div className="relative" data-id="element-2075">
                      <select value={parentId} onChange={e => setParentId(e.target.value)} disabled={pages.length === 0} className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 pr-8 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2076">
                        <option value="" data-id="element-2077">
                          -- Crear en la raíz del espacio --
                        </option>
                        {pages.map(p => <option key={p.id} value={p.id} data-id="element-2078">
                            {p.title}
                          </option>)}
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" data-id="element-2079" />
                    </div>}
                </div>
              </div>

              {/* Title */}
              <div data-id="element-2080">
                <label className="block text-xs font-medium text-slate-400 mb-1.5" data-id="element-2081">
                  Título de la página
                </label>
                <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Ej: Notas de la reunión de Planning" className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2082" />
              </div>

              {/* Labels */}
              <div data-id="element-2083">
                <label className="block text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5" data-id="element-2084">
                  <Tag size={12} data-id="element-2085" /> Etiquetas (separadas por coma)
                </label>
                <input type="text" value={labels} onChange={e => setLabels(e.target.value)} placeholder="Ej: sprint-planning, q1-2026, notas" className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2086" />
              </div>

              {/* Content preview */}
              <div data-id="element-2087">
                <label className="block text-xs font-medium text-slate-400 mb-1.5" data-id="element-2088">
                  Vista previa del contenido (Formato Confluence)
                </label>
                <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-700/80 bg-[#0a0f1a] p-5 text-sm leading-relaxed custom-scrollbar" data-id="element-2089">
                  <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap
                      prose-headings:text-slate-100 prose-headings:font-bold prose-headings:border-b prose-headings:border-slate-800/60 prose-headings:pb-2 prose-headings:mb-3 prose-headings:mt-5 first:prose-headings:mt-0
                      prose-h1:text-lg prose-h2:text-base prose-h3:text-sm
                      prose-p:text-slate-300 prose-p:my-2 prose-p:leading-relaxed
                      prose-strong:text-slate-100
                      prose-em:text-slate-300
                      prose-li:text-slate-300 prose-li:my-0.5
                      prose-ul:my-2 prose-ol:my-2
                      prose-code:text-indigo-300 prose-code:bg-indigo-500/10 prose-code:border prose-code:border-indigo-500/20 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
                      prose-pre:bg-slate-950 prose-pre:border prose-pre:border-slate-800 prose-pre:p-4 prose-pre:rounded-xl prose-pre:whitespace-pre
                      prose-a:text-indigo-400 hover:prose-a:text-indigo-300 prose-a:no-underline hover:prose-a:underline
                      prose-table:w-full prose-table:border-collapse prose-table:my-4
                      prose-th:border prose-th:border-slate-700 prose-th:bg-slate-800/50 prose-th:p-2.5 prose-th:text-left prose-th:text-xs prose-th:text-slate-200 prose-th:font-semibold
                      prose-td:border prose-td:border-slate-800 prose-td:p-2.5 prose-td:text-xs prose-td:text-slate-400
                      prose-blockquote:border-l-2 prose-blockquote:border-indigo-500/50 prose-blockquote:bg-indigo-500/5 prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:not-italic
                      prose-hr:border-slate-800 prose-hr:my-4
                      prose-img:rounded-xl prose-img:border prose-img:border-slate-800" data-id="element-2090">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} data-id="element-2091">
                      {content.length > 1200 ? content.slice(0, 1200) + '\n\n---\n\n*... contenido truncado en la vista previa ...*' : content}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-800 shrink-0 bg-slate-900/50 rounded-b-2xl" data-id="element-2092">
          {result ? <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors border border-slate-700" data-id="element-2093">
              Cerrar
            </button> : <>
              <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-xl transition-colors" data-id="element-2094">
                Cancelar
              </button>
              <button onClick={handleExport} disabled={exporting || !title.trim() || !spaceId} className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors flex items-center gap-2 shadow-lg shadow-indigo-500/20" data-id="element-2095">
                {exporting ? <>
                    <Loader2 size={16} className="animate-spin" data-id="element-2096" />
                    Exportando...
                  </> : <>
                    <FileDown size={16} data-id="element-2097" />
                    Crear Página
                  </>}
              </button>
            </>}
        </div>
      </div>
    </div>;
}